/** One ranked member row inside the position pop-out. */
import { PlusIcon } from "lucide-react-native";
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Button } from "@/components/atoms/Button";
import { Dot } from "@/components/atoms/Dot";
import { Stack } from "@/components/atoms/Stack";
import { Text } from "@/components/atoms/Text";
import WatchlistOrderControls from "@/features/scouting/components/molecules/WatchlistOrderControls";
import type { WatchlistMoveDestination } from "@/features/scouting/components/molecules/WatchlistOrderControls/WatchlistOrderControlsContainer";
import { formatPitchGpr } from "@/features/scouting/utils/watchlistPitchPresentation";

/** One ranked member of the open position group. */
export interface PositionPopoutRow {
  readonly playerId: string;
  readonly displayName: string;
  /**
   * Rank within the position, 1..N. `null` for a watchlist ● squad row
   * (SE-8535): the squad is not in the list's order, so it carries neither a
   * rank nor order controls.
   */
  readonly rank: number | null;
  readonly gpr: number | null;
  /**
   * SE-8393: the scenario's club column — "Rivals FC", or "Your club · CB" for
   * a squad player. Watchlist reads carry the nullable club name (SE-8503).
   */
  readonly club?: string | null;
  /** SE-8393: ● already in the squad, as the scenario legend says. */
  readonly inSquad?: boolean;
}

/** Where one row's action stands (PV22): in flight, refused, or unsaved. */
export interface PositionPopoutRowPending {
  readonly status: "adding" | "failed" | "unsaved";
  /** The backend's message, verbatim, when refused; the reason when unsaved. */
  readonly message: string | null;
}

/**
 * PV22 (SE-8372): an optional per-row action — the blue + that places the
 * target in the scenario at the pop-out's slot. Absent on the watchlist
 * pop-out and for a viewer.
 */
export interface PositionPopoutRowAction {
  /** Accessible label of the +, e.g. "Add to scenario at RW". */
  readonly label: string;
  readonly onPress: (playerId: string) => void;
  /** In-flight / refused / unsaved Adds by player id. */
  readonly pending: Readonly<Record<string, PositionPopoutRowPending>>;
}

/** The action as the View receives it: already bound to this row. */
export interface BoundRowAction {
  readonly label: string;
  readonly onPress: () => void;
  readonly pending: PositionPopoutRowPending | undefined;
}

/** Prepared props for the row presentation. */
export interface WatchlistPositionPopoutRowViewProps {
  readonly row: PositionPopoutRow;
  readonly canEdit: boolean;
  readonly paused: boolean;
  readonly isFirst: boolean;
  readonly isLast: boolean;
  readonly onMove: (destination: WatchlistMoveDestination) => void;
  /** PV22: the + per row, when the opening offers one (bound by the container). */
  readonly rowAction?: BoundRowAction;
}

/**
 * The line under the name while an Add is in flight, refused or unsaved.
 * @param pending - The row's state
 * @returns The caption, or null when idle.
 */
const pendingLine = (pending: PositionPopoutRowPending): string | null =>
  pending.status === "adding" ? "Adding…" : pending.message;

/**
 * Renders the row's + and its status line.
 * @param row - The row
 * @param rowAction - The action
 * @returns The action cluster.
 */
const renderRowAction = (row: PositionPopoutRow, rowAction: BoundRowAction) => {
  const pending = rowAction.pending;
  const line = pending ? pendingLine(pending) : null;
  return (
    <Stack className="items-end gap-micro">
      <Button
        testID={`watchlist-position-popout-add-${row.playerId}`}
        accessibilityLabel={rowAction.label}
        icon={PlusIcon}
        iconOnly
        tone="accent"
        size="xs"
        shape="rounded"
        isDisabled={pending?.status === "adding"}
        onPress={rowAction.onPress}
      />
      {line ? (
        <Text
          testID={`watchlist-position-popout-add-status-${row.playerId}`}
          variant="micro"
          className={
            pending?.status === "failed"
              ? "text-status-error"
              : "text-content-muted"
          }
        >
          {line}
        </Text>
      ) : null}
    </Stack>
  );
};

/**
 * Renders the reorder handle, rank, name and GPR of one group member.
 * @param props - Prepared row props.
 * @param props.row - Ranked member rendered by this row.
 * @param props.canEdit - Whether reorder controls are enabled.
 * @param props.paused - Whether a search pauses reordering.
 * @param props.isFirst - Whether the row is first in the group.
 * @param props.isLast - Whether the row is last in the group.
 * @param props.onMove - Moves this player relative to the group.
 * @param props.rowAction - PV22: the + per row, when offered.
 */
const WatchlistPositionPopoutRowView = ({
  row,
  canEdit,
  paused,
  isFirst,
  isLast,
  onMove,
  rowAction,
}: WatchlistPositionPopoutRowViewProps) => (
  // SE-8486 (W73/S33): a full-bleed row on a 1 px divider, inset 20 — 44
  // tall on the watchlist, 48 when it carries the scenario's 28 px add
  // button (the frame's 52 has no closed rung; ties round down). It used to
  // be a 460-wide bordered card.
  <Stack
    testID={`watchlist-position-popout-row-${row.playerId}`}
    direction="horizontal"
    className={`items-center gap-2 border-b border-outline-default px-5 ${
      rowAction ? "min-h-12" : "min-h-11"
    }`}
  >
    {row.rank === null ? (
      // The rank column's width, so ● names line up with ranked ones.
      <Box className="w-5" />
    ) : (
      <WatchlistOrderControls
        playerId={row.playerId}
        playerName={row.displayName}
        rank={row.rank}
        canEdit={canEdit}
        paused={paused}
        isFirst={isFirst}
        isLast={isLast}
        onMove={onMove}
      />
    )}
    {row.inSquad ? (
      <Dot
        testID={`watchlist-position-popout-squad-${row.playerId}`}
        className="bg-content-muted"
      />
    ) : null}
    <Text
      variant="body-strong"
      className="min-w-0 flex-1 text-content-primary"
      isTruncated
    >
      {row.displayName}
    </Text>
    {/* SE-8486 (W73/S33, Lot D9): the club is a COLUMN — caption, muted,
        right-aligned before the GPR chip — not a line under the name. The
        watchlist pop-out uses the same column when the read supplies club. */}
    {row.club ? (
      <Text
        testID={`watchlist-position-popout-club-${row.playerId}`}
        variant="caption"
        className="shrink text-right text-content-muted"
        isTruncated
      >
        {row.club}
      </Text>
    ) : null}
    {/* W73: a 28×18 info-surface chip with a caption label — `h-4` (16) is
        the closed rung (ties round down), `min-w-7` the 28. */}
    <Box
      testID={`watchlist-position-popout-gpr-${row.playerId}`}
      className="h-4 min-w-7 items-center justify-center rounded-sm bg-status-info-surface px-1"
    >
      <Text variant="caption" className="text-status-info-strong">
        GPR {formatPitchGpr(row.gpr)}
      </Text>
    </Box>
    {rowAction ? renderRowAction(row, rowAction) : null}
  </Stack>
);

WatchlistPositionPopoutRowView.displayName = "WatchlistPositionPopoutRowView";
export default memo(WatchlistPositionPopoutRowView);
